import { AuthContext, delay } from '@repo-level/core-front';
import type { Lootbox, LootboxItem } from 'api/common';
import { LootboxStage, playerLootboxActivation } from 'api/common';
import { ModalType } from 'contexts/modal';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import {
  CLOVER_CASUAL_LOOTBOX_GROUP_KEY,
  CLOVER_VIP_LOOTBOX_GROUP_KEY,
} from 'modules/clover-gold/helpers';
import {
  EXTRA_SPIN_LOOTBOX_TITLES,
  WheelStatusEnum,
} from 'modules/wheel/components/helpers';
import { useCallback, useContext, useMemo } from 'react';

export const EXCLUDED_LOOTBOX_GROUP_KEYS = [
  CLOVER_VIP_LOOTBOX_GROUP_KEY,
  CLOVER_CASUAL_LOOTBOX_GROUP_KEY,
];

export const useLuckyWheel = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { lootboxes } = useContext(RealTimeServiceContext);

  const lootbox = useMemo(() => {
    if (!isAuthenticated) return null;
    return (
      lootboxes.find(
        ({ stage, groupKey }) =>
          stage === LootboxStage.Issued &&
          !EXCLUDED_LOOTBOX_GROUP_KEYS.includes(groupKey)
      ) || null
    );
  }, [lootboxes, isAuthenticated]);

  const isExtraLootbox = useMemo(() => {
    if (!lootbox) return false;
    return EXTRA_SPIN_LOOTBOX_TITLES.includes(lootbox.title);
  }, [lootbox]);

  return { lootbox, isExtraLootbox };
};

interface IUseSpinWheelProps {
  lootbox: Lootbox | null;
  setWheelStatus: (status: WheelStatusEnum | null) => void;
  setWinningPrizeIndex?: (index: number) => void;
  setLootboxItem?: (item: LootboxItem | null) => void;
  fetchLootboxes?: () => void;
}

export const useSpinWheel = ({
  lootbox,
  setWheelStatus,
  setWinningPrizeIndex,
  setLootboxItem,
  fetchLootboxes,
}: IUseSpinWheelProps) => {
  const handleSpin = useCallback(async () => {
    if (!lootbox) return;

    setWheelStatus(WheelStatusEnum.Loading);

    try {
      const { data } = await playerLootboxActivation(lootbox.id);

      const index = lootbox.items.findIndex((item) => item.id === data.id);
      setWinningPrizeIndex?.(index);

      setWheelStatus(WheelStatusEnum.Spinning);

      await delay(7000);
      setLootboxItem?.(data);

      await delay(2000);
      setWheelStatus(WheelStatusEnum.Finished);

      fetchLootboxes?.();
    } catch {
      setWheelStatus(null);
    }
  }, [
    lootbox,
    setWheelStatus,
    setWinningPrizeIndex,
    setLootboxItem,
    fetchLootboxes,
  ]);

  return { handleSpin };
};

interface IUseWheelButtonHandler {
  lootbox: Lootbox | null;
  handleSpin: () => Promise<void>;
  handleModal: (type: ModalType) => () => void;
  handleBonus: () => void;
}

export const useWheelButtonHandler = ({
  lootbox,
  handleSpin,
  handleModal,
  handleBonus,
}: IUseWheelButtonHandler) => {
  const { isAuthenticated } = useContext(AuthContext);
  const handleClickWheelButton = useCallback(async () => {
    if (!isAuthenticated) {
      handleModal(ModalType.Registration)();
      return;
    }

    if (!lootbox) {
      handleBonus();
      return;
    }

    await handleSpin();
  }, [isAuthenticated, lootbox, handleSpin, handleModal, handleBonus]);

  return { handleClickWheelButton };
};
